// Utils
import { ListingsSearchType, SearchPageContentType } from '@gql';

/**
 * Thrown when a caller attempts to encode a slug that would resolve to a
 * different URL path than the caller intended. The two known shapes:
 *
 *   1. Dot-segment slugs (`.`, `..`, `%2E%2E`, percent-decoded variants)
 *      survive `encodeURIComponent` because `.` is unreserved per RFC 3986.
 *      They then get normalized away when `canonicalUrlForPath` runs
 *      `new URL(path, base)`, rewriting the resulting URL to an unintended
 *      path (e.g. `slug='..'` → `/accounts/..` → `https://www.savvy.com/`).
 *   2. Slugs containing `/` (or that decode to contain `/`) implicitly
 *      smuggle additional path segments past callers that expect a single
 *      segment.
 *
 * Catch this at known call sites (sitemap loops should skip the row,
 * page/md handlers should `notFound()`) so a single bad slug doesn't
 * silently corrupt the canonical URL surface or crash a whole sitemap.
 */
export class InvalidSlugError extends Error {
  constructor(slug: string) {
    super(`Invalid slug: ${JSON.stringify(slug)}`);
    this.name = 'InvalidSlugError';
  }
}

/**
 * Throws `InvalidSlugError` if `s` cannot be safely interpolated into a
 * single URL path segment. Exported so call sites can validate untrusted
 * slugs at the boundary (URL params, GraphQL responses) without having to
 * call a route builder.
 */
export function assertValidSlug(s: string): void {
  const invalid = () => new InvalidSlugError(s);
  if (s === '.' || s === '..') {
    throw invalid();
  }
  let decoded: string;
  try {
    decoded = decodeURIComponent(s);
  } catch {
    // Malformed percent-encoding (e.g. `'a%b'`).
    throw invalid();
  }
  // Verify the decoded form is itself safely re-encodable. encodeURIComponent
  // throws URIError on malformed UTF-16 (lone surrogates like '\uD800');
  // converting it here means downstream `instanceof InvalidSlugError` checks
  // still catch the case (and 404/skip-row instead of bubbling as a 500).
  try {
    encodeURIComponent(decoded);
  } catch {
    throw invalid();
  }
  if (decoded === '.' || decoded === '..' || decoded.includes('/')) {
    throw invalid();
  }
}

/**
 * Encode a single URL path segment. Use everywhere a slug or id is
 * interpolated into a route. Centralizing this avoids divergence between
 * HTML routes and their /md siblings (and prevents double-encoding when
 * call sites also encode).
 *
 * Throws `InvalidSlugError` (see above) on dot-segment slugs and slugs
 * that decode to contain `/`. Callers should be prepared to either let
 * the error bubble (request-scoped handlers) or catch it (per-row
 * iteration where one bad row must not poison the whole batch).
 */
const pathSegment = (s: string) => {
  assertValidSlug(s);
  // Belt-and-suspenders: assertValidSlug already exercises encodeURIComponent
  // on the decoded form, but the input itself may still be a lone-surrogate
  // sequence that decodes to itself (decodeURIComponent never touches non-%
  // characters). Wrap the final encode so any residual URIError surfaces as
  // InvalidSlugError, matching what every call site already catches.
  try {
    return encodeURIComponent(s);
  } catch {
    throw new InvalidSlugError(s);
  }
};

export const routes = {
  home: '/',
  jointTheRevolution: '/jointherevolution',
  savvyIsTheFuture: '/future',
  fourthOfJuly: '/fourth-of-july',
  accounts: (slug: string) => `/accounts/${pathSegment(slug)}`,
  accountMarkdown: (slug: string) =>
    `/api/accounts/${pathSegment(slug)}/md`,
  listings: ({
    slug,
    checkInDate,
    checkOutDate,
    adultCount,
    childCount,
    infantCount,
    petCount,
  }: {
    slug: string;
    checkInDate?: string;
    checkOutDate?: string;
    adultCount?: number;
    childCount?: number;
    infantCount?: number;
    petCount?: number;
  }) => {
    let queryParams = new URLSearchParams();

    if (adultCount === undefined || adultCount === 0) {
      adultCount = 1;
    }

    if (checkInDate !== undefined) {
      queryParams.set('checkInDate', checkInDate);
    }

    if (checkOutDate !== undefined) {
      queryParams.set('checkOutDate', checkOutDate);
    }

    queryParams.set('adultCount', adultCount.toString());

    if (childCount !== undefined) {
      queryParams.set('childCount', childCount.toString());
    }

    if (infantCount !== undefined) {
      queryParams.set('infantCount', infantCount.toString());
    }

    if (petCount !== undefined) {
      queryParams.set('petCount', petCount.toString());
    }

    return concatParams(`/listings/${pathSegment(slug)}`, queryParams);
  },
  comingSoon: '/a/coming-soon',
  searchShow: ({
    placeId,
    searchType,
    centerLat,
    centerLng,
    southWestLat,
    southWestLng,
    northEastLat,
    northEastLng,
    zoom,
    activeFeatures,
    checkInDate,
    checkOutDate,
    adultCount = 1,
    childCount = 0,
    infantCount = 0,
    petCount = 0,
    bedroomCount = 0,
    bathroomCount = 0,
    bedCount = 0,
    activePropertyTypes,
  }: {
    placeId?: string;
    searchType?: string;
    centerLat?: string;
    centerLng?: string;
    southWestLat?: string;
    southWestLng?: string;
    northEastLat?: string;
    northEastLng?: string;
    zoom?: string;
    activeFeatures?: string;
    checkInDate?: string;
    checkOutDate?: string;
    adultCount?: number;
    childCount?: number;
    infantCount?: number;
    petCount?: number;
    bedroomCount?: number;
    bathroomCount?: number;
    bedCount?: number;
    activePropertyTypes?: string;
  }) => {
    let queryParams = new URLSearchParams();

    if (placeId) {
      queryParams.set('placeId', placeId);
    }

    if (searchType) {
      queryParams.set('searchType', searchType);
    }

    if (centerLat) {
      queryParams.set('centerLat', centerLat);
    }

    if (centerLng) {
      queryParams.set('centerLng', centerLng);
    }

    if (southWestLat) {
      queryParams.set('southWestLat', southWestLat);
    }

    if (southWestLng) {
      queryParams.set('southWestLng', southWestLng);
    }

    if (northEastLat) {
      queryParams.set('northEastLat', northEastLat);
    }

    if (northEastLng) {
      queryParams.set('northEastLng', northEastLng);
    }

    if (zoom) {
      queryParams.set('zoom', zoom);
    }

    if (activeFeatures) {
      queryParams.set('activeFeatures', activeFeatures);
    }

    if (checkInDate !== undefined) {
      queryParams.set('checkInDate', checkInDate);
    }

    if (checkInDate === undefined || null) {
      queryParams.set('checkInDate', '');
    }

    if (checkOutDate !== undefined) {
      queryParams.set('checkOutDate', checkOutDate);
    }

    if (checkOutDate === undefined || null) {
      queryParams.set('checkOutDate', '');
    }

    if (adultCount === undefined || adultCount === 0) {
      adultCount = 1;
    }

    queryParams.set('adultCount', adultCount.toString());
    queryParams.set('childCount', childCount.toString());
    queryParams.set('infantCount', infantCount.toString());
    queryParams.set('petCount', petCount.toString());

    queryParams.set('minNumberOfRooms', bedroomCount.toString());
    queryParams.set('minNumberOfBathrooms', bathroomCount.toString());
    queryParams.set('minNumberOfBeds', bedCount.toString());

    if (activePropertyTypes) {
      queryParams.set('propertyTypes', activePropertyTypes);
    }

    return concatParams('/search', queryParams);
  },
  sitemaps: (searchType: SearchPageContentType) => {
    let slug: string;
    switch (searchType) {
      case SearchPageContentType.VacationRental:
        slug = 'vacation-rentals';
        break;
      default:
        slug = 'vacation-rentals';
    }

    return `/sitemaps/${slug}`;
  },
  seoVRSearchShow: (region: {
    countrySlug?: string;
    levelOneSlug?: string;
    citySlug?: string;
    slug?: string;
  }) => {
    return `/vacation-rentals/${regionToPath(region)}`;
  },
  /**
   * Build a `/vacation-rentals/...` URL from an arbitrary segments array.
   * Used by the `[...place]` catch-all when it needs to construct a redirect
   * target without going through the structured `seoVRSearchShow` shape
   * (e.g. case-canonicalization 308s where `place.length` may be > 4 and we
   * just want each segment lowercased and re-encoded).
   *
   * Encodes via `encodeURIComponent` only — does NOT call `assertValidSlug`,
   * because the catch-all route's strict-guard regex (`PLACE_SLUG_RE` in
   * `(public)/vacation-rentals/[...place]/page.tsx`) is the source of truth
   * for what shapes are allowed. Threading `assertValidSlug` here would
   * surface dot-segment slugs as 500s rather than 404s and diverge from
   * the existing `buildPlaceUrlPath` behavior.
   */
  vacationRentalsForSegments: (segments: string[]) =>
    '/vacation-rentals/' + segments.map(encodeURIComponent).join('/'),
  /**
   * Markdown sibling of `vacationRentalsForSegments` — emits
   * `/api/regions/md/{a}/{b}/{c}`. Same per-segment encoding rule as
   * the HTML route: `encodeURIComponent` only, no `assertValidSlug`.
   * The page-side regex (`PLACE_SLUG_RE` and the boundary filter
   * `isUrlSafeRegionPath` in `@utils/sitemap-region-path`) is the
   * source of truth for what shapes are allowed; threading
   * `assertValidSlug` here would surface dot-segment slugs as 500s
   * rather than 404s and diverge from the HTML route's behavior.
   *
   * URL shape note: the listings/accounts /md siblings put the slug
   * BEFORE `/md` (`/api/listings/{slug}/md`), but Next.js requires
   * catch-all dynamic segments to be the LAST part of the URL — so
   * `/api/regions/[...path]/md` fails the build with "Catch-all must
   * be the last part of the URL". `md` therefore comes BEFORE the
   * catch-all here. Functionally equivalent for AI-bot discovery.
   *
   * BHT-6315.
   */
  regionMarkdown: (segments: string[]) =>
    '/api/regions/md/' + segments.map(encodeURIComponent).join('/'),
  accountListingsSearchShow: ({
    slug,
    checkInDate,
    checkOutDate,
    bedroomCount = 0,
    bathroomCount = 0,
    bedCount = 0,
    activePropertyTypes,
    activeUniquePropertyTypes,
  }: {
    slug: string;
    checkInDate?: string;
    checkOutDate?: string;
    bedroomCount?: number;
    bathroomCount?: number;
    bedCount?: number;
    activePropertyTypes?: string;
    activeUniquePropertyTypes?: string;
  }) => {
    let queryParams = new URLSearchParams();

    if (checkInDate) {
      queryParams.set('checkInDate', checkInDate);
    }

    if (checkOutDate) {
      queryParams.set('checkOutDate', checkOutDate);
    }

    if (bedroomCount) {
      queryParams.set('minNumberOfRooms', bedroomCount.toString());
    }

    if (bathroomCount) {
      queryParams.set('minNumberOfBathrooms', bathroomCount.toString());
    }

    if (bedCount) {
      queryParams.set('minNumberOfBeds', bedCount.toString());
    }

    if (activePropertyTypes) {
      queryParams.set('propertyTypes', activePropertyTypes);
    }

    if (activeUniquePropertyTypes) {
      queryParams.set('uniquePropertyTypes', activeUniquePropertyTypes);
    }

    queryParams.set('searchType', ListingsSearchType.Map);
    queryParams.set('northEastLat', '61.01572493360369');
    queryParams.set('northEastLng', '-66.884766');
    queryParams.set('southWestLat', '2.4601814276672704');
    queryParams.set('southWestLng', '-124.541016');
    queryParams.set('centerLat', '31.737953180635483');
    queryParams.set('centerLng', '-95.712891');
    queryParams.set('zoom', '3');

    return concatParams(`/search/account/${slug}`, queryParams);
  },
  help: 'https://finderco.my.site.com/help/s/',
  travlerHelp: 'https://finderco.my.site.com/help/s/travelers',
  hostHelp: 'https://finderco.my.site.com/help/s/professional-hosts',
  noFeesDisclaimer:
    'https://finderco.my.site.com/help/s/article/The-bnbfinder-Difference',
  giftCards: 'https://finderco.my.site.com/help/s/savvy-gift-cards',
  giftCardRedemption:
    'https://finderco.my.site.com/help/s/savvy-gift-cards/travelers?tabset-7ec7a=3',
  barefootHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/barefoot',
  boostHelp: 'https://finderco.my.site.com/help/s/partner-integrations/boost',
  ciirusHelp: 'https://finderco.my.site.com/help/s/partner-integrations/ciirus',
  escapiaHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/escapia',
  guestyHelp: 'https://finderco.my.site.com/help/s/partner-integrations/guesty',
  hostawayHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/hostaway',
  hostfullyHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/hostfully',
  hostfullyOAuthCallback: '/oauth/hostfully/callback',
  hostfullyOAuthStart: '/oauth/hostfully/start',
  hospitableOAuthCallback: '/oauth/hospitable/callback',
  lodgifyHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/lodgify',
  ownerRezHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/ownerrez',
  quickConnectHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/quickconnect',
  streamlineHelp:
    'https://finderco.my.site.com/help/s/partner-integrations/streamline',
  trackHelp: 'https://finderco.my.site.com/help/s/partner-integrations/track',
  integrationHelp: (integration: string) =>
    `https://finderco.my.site.com/help/s/partner-integrations/${integration}`,
  signup: (args?: { redirect?: string; ref?: string }) => {
    const params = new URLSearchParams();
    if (args?.redirect) params.set('redirect', args.redirect);
    if (args?.ref) params.set('ref', args.ref);
    const qs = params.toString();
    return qs ? `/signup?${qs}` : '/signup';
  },
  login: '/login',
  loginWithRedirect: (args?: { redirect?: string; source?: string }) => {
    if (args && args.redirect && args.source) {
      return `/login?source=${encodeURIComponent(
        args.source
      )}&callbackUrl=${encodeURIComponent(args.redirect)}`;
    } else if (args && args.redirect) {
      return `/login?callbackUrl=${encodeURIComponent(args.redirect)}`;
    } else {
      return '/login';
    }
  },
  list: '/list',
  boost: '/boost',
  createAccount: (args?: { redirect?: string }) => {
    if (args && args.redirect) {
      return `/create-account?redirect=${encodeURIComponent(args.redirect)}`;
    } else {
      return '/create-account';
    }
  },
  fiftyCashbackPromotionTerms: '/50-cashback-promotion-terms',
  referAndEarn: '/refer',
  about: '/about',
  howItWorks: '/how-it-works',
  savvyPromise: '/savvy-promise',
  faq: '/faq',
  faqCategory: (category: string) => `/faq/${pathSegment(category)}`,
  faqQuestion: (category: string, question: string) =>
    `/faq/${pathSegment(category)}/${pathSegment(question)}`,
  iosApp: '/ios-app',
  blog: '/journal',
  /**
   * Markdown endpoint for an individual journal post. Mirrors the
   * `listingMarkdown` / `accountMarkdown` pattern (single-slug, encoded
   * via `pathSegment` so `..`, `%2E%2E`, lone-surrogate, and other
   * dot-segment / malformed slugs are rejected with `InvalidSlugError`
   * at the boundary — see `assertValidSlug` above).
   *
   * BHT-6315.
   */
  journalMarkdown: (slug: string) =>
    `/api/journal/${pathSegment(slug)}/md`,
  press: '/press',
  integrations: '/integrations',
  partnerIntegrationShow: (id: string) => `/integrations/${id}`,
  accessibility: '/accessibility',
  search: '/search',
  mobileSearch: '/m/search',
  privacy: '/privacy',
  terms: '/terms',
  listingTerms: (slug: string) => `/listings/${pathSegment(slug)}/terms`,
  listingMarkdown: (slug: string) =>
    `/api/listings/${pathSegment(slug)}/md`,
  passwordForgot: '/password/forgot',
  passwordReset: '/password/reset',
  accountActions: '/account-actions',
  userAccountSettings: '/account-settings',
  userAccountFavorites: '/account-favorites',
  intakeForm: '/request-a-call',
  accountListings: '/a/listings',
  accountProfile: '/a/profile',
  accountListingsNew: '/a/listings/new',
  apiAuthLogin: '/api/auth/login',
  apiAuthSocial: '/api/auth/social',
  apiAuthSso: (token: string) => `/api/auth/sso/?q=${token}`,
  accountBilling: '/a/billing',
  accountCommissions: '/a/commissions',
  accountPaymentMethodsUpdate: (args?: { redirectUrl?: string }) => {
    if (args && args.redirectUrl) {
      return `/a/payment-methods/update/?redirectUrl=${encodeURIComponent(
        args.redirectUrl
      )}`;
    } else {
      return '/a/payment-methods/update';
    }
  },
  accountListingsDetails: (id: string) => `/a/listings/${id}/details`,
  accountFriendsAndFamilyPageAccess: (id: string) =>
    `/a/listings/${id}/friends-and-family-page-access`,
  onboardingJoin: '/onboarding/step-1-join',
  onboardingImportListings: '/onboarding/step-2-import-listings',
  onboardingCodeOfConduct: '/onboarding/step-3-code-of-conduct',
  onboardingSubscribe: '/onboarding/step-4-subscribe',
  onboardingReview: '/onboarding/step-5-review',
  onboardingNotSupported: '/onboarding/not-supported',
  contactSupport: 'https://finderco.my.site.com/help/s/contactsupport',
  feedback: 'https://finderco.my.site.com/help/s/feedback',
  stripeCustomer(customerId: string) {
    return `https://dashboard.stripe.com/customers/${customerId}`;
  },
  stripeListingSubscription(subscriptionId: string) {
    return `https://dashboard.stripe.com/subscriptions/${subscriptionId}`;
  },
  book: ({
    // slug will be changed to id
    slug,
    checkInDate,
    checkOutDate,
    adultCount,
    childCount,
    infantCount,
    petCount,
  }: {
    slug: string;
    checkInDate?: string;
    checkOutDate?: string;
    adultCount?: number;
    childCount?: number;
    infantCount?: number;
    petCount?: number;
  }) => {
    let queryParams = new URLSearchParams();

    if (adultCount === undefined || adultCount === 0) {
      adultCount = 1;
    }

    if (checkInDate && checkInDate !== undefined) {
      queryParams.set('checkInDate', checkInDate);
    }

    if (checkOutDate && checkOutDate !== undefined) {
      queryParams.set('checkOutDate', checkOutDate);
    }

    queryParams.set('adultCount', adultCount.toString());

    if (childCount !== undefined) {
      queryParams.set('childCount', childCount.toString());
    }

    if (infantCount !== undefined) {
      queryParams.set('infantCount', infantCount.toString());
    }

    if (petCount !== undefined) {
      queryParams.set('petCount', petCount.toString());
    }

    return concatParams(`/book/${pathSegment(slug)}`, queryParams);
  },
  trips: '/trips',
  tripDetails: (reservationId: string) => {
    return `/trips/${reservationId}`;
  },
  cancelTrip: (reservationId: string) => `/trips/${reservationId}/cancel`,
  facebook: 'https://www.facebook.com/savvystays',
  x: 'https://x.com/savvy_stays',
  instagram: 'https://www.instagram.com/savvy_stay',
  youtube: 'https://www.youtube.com/@savvystays',
  pinterest: 'https://www.pinterest.com/savvystays/',
  linkedIn: 'https://www.linkedin.com/company/stay-savvy/',
  tiktok: 'https://www.tiktok.com/@savvystays',
  threads: 'https://www.threads.net/@bnbfinder',
  deeplink: (slug: string, redirectUrl: string) =>
    `/deeplink/?slug=${slug}&redirectUrl=${encodeURIComponent(redirectUrl)}`,
} as const;

const regionToPath = ({
  countrySlug,
  levelOneSlug,
  citySlug,
  slug,
}: {
  countrySlug?: string;
  levelOneSlug?: string;
  citySlug?: string;
  slug?: string;
}) => {
  const path = [countrySlug, levelOneSlug, citySlug, slug]
    .filter((value) => value !== null && value !== undefined)
    .join('/');
  return path;
};

const concatParams = (path: string, params: URLSearchParams) => {
  let paramsStr = params.toString();
  if (paramsStr.length > 0) {
    paramsStr = `?${paramsStr}`;
  }
  return `${path}${paramsStr}`;
};

export const canonicalUrlForPath = (path: string) => {
  const url = new URL(path, 'https://www.savvy.com');
  url.search = ''; // Remove query parameters
  return url.toString();
};

export const canonicalUrlForPathWithPage = (path: string, page: number) =>
  page > 1
    ? `${canonicalUrlForPath(path)}?page=${page}`
    : canonicalUrlForPath(path);
